import { ref } from 'vue';

const AUTH_STORAGE_KEY = 'auth_registration_data';

interface AuthFormData {
  email: string;
  password: string;
  passwordConfirmation: string;
  firstName: string;
  lastName: string;
  company: string;
}

interface PersistedAuthData {
  email: string;
  firstName: string;
  lastName: string;
  company: string;
}

export function useAuthState() {
  const formData = ref<AuthFormData>({
    email: '',
    password: '',
    passwordConfirmation: '',
    firstName: '',
    lastName: '',
    company: '',
  });

  const activationCode = ref('');
  const setupToken = ref<string | null>(null);
  const currentStep = ref(1);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const updateFormData = (field: keyof AuthFormData, value: string) => {
    formData.value[field] = value;
  };

  const persistEmail = () => {
    const dataToPersist: PersistedAuthData = {
      email: formData.value.email,
      firstName: formData.value.firstName,
      lastName: formData.value.lastName,
      company: formData.value.company,
    };
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(dataToPersist));
  };

  const getPersistedEmail = (): string | null => {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      try {
        const data: PersistedAuthData = JSON.parse(stored);
        return data.email || null;
      } catch {
        return null;
      }
    }
    return null;
  };

  const getPersistedData = (): PersistedAuthData | null => {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  };

  const loadPersistedData = () => {
    const data = getPersistedData();
    if (data) {
      formData.value.email = data.email || '';
      formData.value.firstName = data.firstName || '';
      formData.value.lastName = data.lastName || '';
      formData.value.company = data.company || '';
    }
  };

  const clearPersistedData = () => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  };

  const clearError = () => {
    error.value = null;
  };

  return {
    formData,
    activationCode,
    setupToken,
    currentStep,
    isLoading,
    error,
    updateFormData,
    persistEmail,
    getPersistedEmail,
    getPersistedData,
    loadPersistedData,
    clearPersistedData,
    clearError,
  };
}
